import { useState, useEffect, useCallback, useMemo } from "react";
import FilterPills from "@/components/FilterPills";
import { defaultAmFor } from "@/lib/amDefault";
import { useAuth } from "@/lib/AuthContext";
import SearchBar from "@/components/SearchBar";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DollarSign, Hash, TrendingUp, TrendingDown, Search, Building2, Target, Trophy, Star, Pencil, Check, X, ArrowUp, ArrowDown, ArrowUpDown, CalendarDays, Copy, BarChart3, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import CompanyForecast from "@/components/CompanyForecast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, BarChart, Bar, Cell } from "recharts";

function fmt$(val) {
  if (val == null) return "$0";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);
}

function fmtPct(val) {
  if (val == null) return "0%";
  return `${val.toFixed(1)}%`;
}

// Toggle configs for Revenue Bid / Booked charts — maps mode -> keys + formatters.
const METRIC_MODES = [
  { id: "sales", label: "Revenue $" },
  { id: "gm", label: "GM $" },
  { id: "gmpct", label: "GM %" },
];

function metricConfig(mode, weekly_goal) {
  if (mode === "gm") {
    return {
      dataKey: "total_gm",
      format: fmt$,
      tooltipLabel: "GM $",
      goal: null,
      barColor: "#3b82f6",
      trailingKeys: { "4 Wk": "trailing_4_gm", "3 Mo": "trailing_3mo_gm", "6 Mo": "trailing_6mo_gm", "12 Mo": "trailing_12mo_gm", "_this_fy": "avg_this_fy_gm", "_last_fy": "avg_last_fy_gm" },
      fyTotalKey: "fy_total_this_gm",
      colorByHit: false,
    };
  }
  if (mode === "gmpct") {
    return {
      dataKey: "avg_gm_pct",
      format: fmtPct,
      tooltipLabel: "GM %",
      goal: null,
      barColor: "#8b5cf6",
      trailingKeys: { "4 Wk": "trailing_4_gm_pct", "3 Mo": "trailing_3mo_gm_pct", "6 Mo": "trailing_6mo_gm_pct", "12 Mo": "trailing_12mo_gm_pct", "_this_fy": "avg_this_fy_gm_pct", "_last_fy": "avg_last_fy_gm_pct" },
      fyTotalKey: "fy_total_this_gm_pct",
      colorByHit: false,
    };
  }
  return {
    dataKey: "total_sales",
    format: fmt$,
    tooltipLabel: "Revenue",
    goal: weekly_goal,
    barColor: null,
    trailingKeys: { "4 Wk": "trailing_4_avg", "3 Mo": "trailing_3mo_avg", "6 Mo": "trailing_6mo_avg", "12 Mo": "trailing_12mo_avg", "_this_fy": "avg_this_fy", "_last_fy": "avg_last_fy" },
    fyTotalKey: "fy_total_this",
    colorByHit: true,
  };
}

function MetricToggle({ value, onChange, testid }) {
  return (
    <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid={testid}>
      {METRIC_MODES.map((m) => (
        <button
          key={m.id}
          onClick={() => onChange(m.id)}
          className={cn(
            "px-2.5 h-6 text-[11px] font-medium transition-colors",
            value === m.id ? "bg-[#007BFF] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
          )}
          data-testid={`${testid}-${m.id}`}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

const GRAIN_MODES = [
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
];

function GrainToggle({ value, onChange, testid }) {
  return (
    <div className="flex rounded-md border border-slate-200 overflow-hidden" data-testid={testid}>
      {GRAIN_MODES.map((m) => (
        <button
          key={m.id}
          onClick={() => onChange(m.id)}
          className={cn(
            "px-2.5 h-6 text-[11px] font-medium transition-colors",
            value === m.id ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"
          )}
          data-testid={`${testid}-${m.id}`}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

function HitRateBar({ value, color }) {
  return (
    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
      <div className={cn("h-full rounded-full transition-all duration-700", color)} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subtitle, color }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", color || "bg-slate-100")}>
          <Icon className="h-4 w-4 text-white" />
        </div>
        <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</span>
      </div>
      <div className="stat-num text-2xl font-semibold text-slate-900">{value}</div>
      {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
    </div>
  );
}

function PeriodTabs({ active, onChange, labels }) {
  return (
    <div className="flex items-center bg-slate-100 rounded-md p-0.5 gap-0.5">
      {Object.entries(labels).map(([key, label]) => (
        <button key={key} className={cn("px-3.5 py-1.5 text-xs font-medium rounded transition-colors", active === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}
          onClick={() => onChange(key)} data-testid={`period-tab-${key}`}>{label}</button>
      ))}
    </div>
  );
}

function SortableHeader({ label, sortKey, sortState, onSort, align }) {
  const current = sortState.key === sortKey ? sortState.dir : null;
  return (
    <button className={cn("flex items-center gap-1 group", align === "right" && "ml-auto")}
      onClick={() => onSort(sortKey)} data-testid={`sort-${sortKey}`}>
      <span>{label}</span>
      <span className="opacity-0 group-hover:opacity-100 transition-opacity">
        {current === "asc" ? <ArrowUp className="h-3 w-3" /> : current === "desc" ? <ArrowDown className="h-3 w-3" /> : <ArrowUpDown className="h-3 w-3 text-slate-400" />}
      </span>
    </button>
  );
}

const RATING_COLORS = { A: "bg-emerald-100 text-emerald-700", B: "bg-blue-100 text-blue-700", C: "bg-amber-100 text-amber-700", D: "bg-red-100 text-red-700" };

export default function DashboardPage() {
  const { user, role, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [top10Data, setTop10Data] = useState(null);
  const [quarterData, setQuarterData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [revenueBidData, setRevenueBidData] = useState(null);
  const [revenueBookedData, setRevenueBookedData] = useState(null);
  const [bidMetric, setBidMetric] = useState("sales"); // 'sales' | 'gm' | 'gmpct'
  const [bookedMetric, setBookedMetric] = useState("sales");
  const [bidGrain, setBidGrain] = useState("week"); // 'week' | 'month'
  const [bookedGrain, setBookedGrain] = useState("week");
  const [reportOpen, setReportOpen] = useState(false);
  const todayIso = new Date().toISOString().slice(0, 10);
  const [reportRange, setReportRange] = useState(() => {
    const now = new Date();
    const fyStartYear = now.getMonth() >= 4 ? now.getFullYear() : now.getFullYear() - 1;
    return { start: `${fyStartYear}-05-01`, end: now.toISOString().slice(0, 10) };
  });
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [amFilter, setAmFilter] = useState("ALL");
  const [estFilter, setEstFilter] = useState("ALL");
  const [filtersReady, setFiltersReady] = useState(false);
  const [period, setPeriod] = useState("all_time");
  const [customerSearch, setCustomerSearch] = useState("");
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalInput, setGoalInput] = useState("");
  const [quarterFy, setQuarterFy] = useState("this");
  const [quarterNum, setQuarterNum] = useState(() => {
    const m = new Date().getMonth() + 1;
    // Updated quarters: Q1=May-Jul, Q2=Aug-Oct, Q3=Nov-Jan, Q4=Feb-Apr
    if (m >= 5 && m <= 7) return 1;
    if (m >= 8 && m <= 10) return 2;
    if (m >= 11 || m <= 1) return 3;
    return 4;
  });
  const [customerSort, setCustomerSort] = useState({ key: "total_bid_revenue", dir: "desc" });

  const fetchData = useCallback(async () => {
    try {
      const [hitRates, monthly, top10, forecast, revBid, revBooked] = await Promise.all([
        api.getHitRates(amFilter, estFilter),
        api.getMonthlyBookings(amFilter, estFilter),
        api.getTop10(amFilter, estFilter),
        api.getRevenueForecast(),
        api.getRevenueBid(),
        api.getRevenueBooked(),
      ]);
      setData(hitRates);
      // Dynamic goal: $1M for single AM, $2M for all AMs
      const dynamicGoal = amFilter === "ALL" ? 2000000 : 1000000;
      setBookings({ ...monthly, goal: dynamicGoal });
      setTop10Data(top10);
      setForecastData(forecast);
      setRevenueBidData(revBid);
      setRevenueBookedData(revBooked);
    } catch (e) {
      console.error("Failed to fetch analytics:", e);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  }, [amFilter, estFilter]);

  // Defaults re-apply on every sign-in: an AM opens on their own initials, everyone else on All.
  useEffect(() => {
    if (authLoading) return;
    setAmFilter(defaultAmFor(role, user?.name)); setEstFilter("ALL"); setFiltersReady(true);
  }, [authLoading, role, user?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (filtersReady) { setLoading(true); fetchData(); } }, [fetchData, filtersReady]);
  useEffect(() => {   // a remembered pick that's no longer in the list falls back to All
    if (!data) return;
    if (amFilter !== "ALL" && data.am_options && !data.am_options.includes(amFilter)) setAmFilter("ALL");
    if (estFilter !== "ALL" && data.est_options && !data.est_options.includes(estFilter)) setEstFilter("ALL");
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch quarter pipeline data
  useEffect(() => {
    let cancelled = false;
    api.getQuarterPipeline(quarterFy, quarterNum)
      .then((d) => { if (!cancelled) setQuarterData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [quarterFy, quarterNum]);

  const companyStats = data?.company?.[period];

  // Build dynamic period labels using FY names from backend
  const periodLabels = useMemo(() => {
    const fyLabels = data?.fy_labels || {};
    return {
      this_fy: fyLabels.this_fy || "This FY",
      last_fy: fyLabels.last_fy || "Last FY",
      all_time: "All Time",
    };
  }, [data]);

  const handleCustomerSort = (key) => {
    setCustomerSort(prev => ({ key, dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc" }));
  };

  const customerRows = useMemo(() => {
    if (!data?.per_customer) return [];
    let rows = Object.entries(data.per_customer)
      .map(([name, periods]) => ({ name, ...periods[period] }))
      .filter((c) => c.total_bid_count > 0)
      .filter((c) => !customerSearch || c.name.toLowerCase().includes(customerSearch.toLowerCase()));
    const { key, dir } = customerSort;
    rows.sort((a, b) => {
      const av = a[key] ?? 0, bv = b[key] ?? 0;
      if (typeof av === "string") return dir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      return dir === "asc" ? av - bv : bv - av;
    });
    return rows;
  }, [data, period, customerSearch, customerSort]);

  const handleGoalSave = async () => {
    const val = parseFloat(goalInput.replace(/[^0-9.]/g, ""));
    if (isNaN(val) || val <= 0) { toast.error("Enter a valid goal amount"); return; }
    try {
      await api.setMonthlyGoal(val);
      setBookings(prev => ({ ...prev, goal: val }));
      setEditingGoal(false);
      toast.success(`Goal updated to ${fmt$(val)}`);
    } catch (e) { toast.error("Failed to update goal"); }
  };

  const top10List = useMemo(() => {
    if (!top10Data) return [];
    if (amFilter === "ALL") return top10Data.overall_top10 || [];
    return (top10Data.per_am_top10 || {})[amFilter] || [];
  }, [top10Data, amFilter]);

  // Copy forecast data to clipboard - month headers on row 1, dollar values on row 2 (tab-separated for Excel paste)
  const handleCopyForecast = useCallback(() => {
    if (!forecastData?.months?.length) return;
    const headers = forecastData.months.map(m => m.month_label).join("\t");
    const values = forecastData.months.map(m => "$" + Math.round(m.amount).toLocaleString("en-US")).join("\t");
    const clipText = headers + "\n" + values;
    const textarea = document.createElement("textarea");
    textarea.value = clipText;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
      toast.success("Forecast copied to clipboard!");
    } catch {
      toast.error("Failed to copy to clipboard");
    }
    document.body.removeChild(textarea);
  }, [forecastData]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center" data-testid="dashboard-loading">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-2 border-[#007BFF] border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-500">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  const bookingPct = bookings ? Math.min(100, (bookings.total_forecasted / (bookings.goal || 1)) * 100) : 0;

  const downloadReport = async () => {
    if (!reportRange.start || !reportRange.end) {
      toast.error("Pick a start and end date");
      return;
    }
    if (reportRange.start > reportRange.end) {
      toast.error("Start date must be on or before end date");
      return;
    }
    setDownloading(true);
    try {
      const blob = await api.exportRevenueBooked(reportRange.start, reportRange.end);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `revenue_booked_${reportRange.start}_to_${reportRange.end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
      setReportOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="page-body" data-testid="dashboard-page">
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center justify-start">
          <FilterPills testId="dashboard-filter-pills" pills={[
            { key: "dashboard-am", label: "AM", options: (data?.am_options || []).map((a) => ({ value: a, label: a })),
              value: amFilter === "ALL" ? "" : amFilter, onChange: (v) => setAmFilter(v || "ALL") },
            { key: "dashboard-est", label: "Est", options: (data?.est_options || []).map((e) => ({ value: e, label: e })),
              value: estFilter === "ALL" ? "" : estFilter, onChange: (v) => setEstFilter(v || "ALL") },
          ]} />
        </div>

        {/* MONTH BOOKINGS + TOP 10 ROW */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Month Bookings */}
          <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="monthly-bookings">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Target className="h-4 w-4 text-[#007BFF]" />
                Month Bookings — {bookings?.month_label || ""}
              </h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Goal: {fmt$(bookings?.goal)}</span>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-end justify-between">
                <div>
                  <div className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="bookings-total">{fmt$(bookings?.total_forecasted)}</div>
                  <p className="text-xs text-slate-500 mt-0.5">of {fmt$(bookings?.goal)} goal</p>
                </div>
                <span className={cn("text-lg font-bold", bookingPct >= 100 ? "text-emerald-600" : bookingPct >= 50 ? "text-amber-600" : "text-slate-500")}>{bookingPct.toFixed(0)}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-6 overflow-hidden relative">
                <div className={cn("h-full rounded-full transition-all duration-1000", bookingPct >= 100 ? "bg-emerald-500" : bookingPct >= 50 ? "bg-[#007BFF]" : "bg-[#007BFF]/60")}
                  style={{ width: `${bookingPct}%` }} data-testid="bookings-bar" />
              </div>
              {bookings?.won_projects?.length > 0 ? (
                <div className="mt-3 space-y-1.5">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Won this month</p>
                  {bookings.won_projects.map((p, i) => (
                    <div key={i} className="flex items-center justify-between py-1 text-xs">
                      <span className="text-slate-700 truncate max-w-[240px]">{p.name}</span>
                      <span className="text-slate-500 font-medium tabular-nums">{fmt$(p.forecasted_revenue)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 mt-2">No projects won this month yet</p>
              )}
            </div>
          </div>

          {/* Top 10 */}
          <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="top-10-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Trophy className="h-4 w-4 text-amber-500" />
                Top 10 Priority Projects
              </h3>
            </div>
            <div className="max-h-[340px] overflow-auto">
              {top10List.length > 0 ? top10List.map((p, i) => (
                <div key={i} className="flex items-center gap-3 py-2 border-b border-slate-50 last:border-0" data-testid={`top10-row-${i}`}>
                  <div className={cn("h-7 w-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0",
                    i === 0 ? "bg-amber-100 text-amber-700" : i === 1 ? "bg-slate-200 text-slate-600" : i === 2 ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-500"
                  )}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{p.name}</div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-slate-400">{p.customer_name}</span>
                      <span className="text-[10px] text-slate-300">|</span>
                      <span className="text-[10px] text-slate-400">{p.account_manager || "—"}</span>
                      {p.client_rating && (
                        <>
                          <span className="text-[10px] text-slate-300">|</span>
                          <Badge className={cn("rounded-full px-1.5 py-0 text-[9px] font-semibold border-0 shadow-none", RATING_COLORS[p.client_rating])}>{p.client_rating}</Badge>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs font-medium text-slate-700 tabular-nums">{fmt$(p.forecasted_revenue)}</div>
                    <div className="text-[10px] text-slate-400">{p.profit_margin}% margin</div>
                  </div>
                  <div className="w-14 text-right shrink-0">
                    <div className="flex items-center gap-1 justify-end">
                      <Star className="h-3 w-3 text-amber-400" />
                      <span className="text-xs font-bold text-slate-700">{p.score}</span>
                    </div>
                  </div>
                </div>
              )) : (
                <p className="text-xs text-slate-400 text-center py-8">No projects to score</p>
              )}
            </div>
          </div>
        </div>

        {/* REVENUE BID - Weekly Bar Chart */}
        {revenueBidData && (() => {
          const cfg = metricConfig(bidMetric, revenueBidData.weekly_goal);
          const useMonth = bidGrain === "month";
          const allWeeks = useMonth ? (revenueBidData.months || []) : revenueBidData.weeks;
          const activeGoal = useMonth ? (revenueBidData.monthly_goal || cfg.goal * 4.33) : cfg.goal;
          // Y-axis max based on selected metric
          const dataMax = Math.max(0, ...allWeeks.map(w => w[cfg.dataKey] || 0));
          const goalRef = cfg.goal || 0;
          let yDomain;
          if (bidMetric === "gmpct") {
            yDomain = [0, Math.max(50, Math.ceil(dataMax / 10) * 10)];
          } else if (bidMetric === "gm") {
            const step = useMonth ? 500000 : 100000;
            yDomain = [0, Math.max(step, Math.ceil(dataMax / step) * step)];
          } else {
            const step = useMonth ? 2000000 : 500000;
            yDomain = [0, Math.ceil(Math.max(dataMax, (activeGoal || 0) * 1.1) / step) * step];
          }
          const trailingMetrics = [
            { label: "4 Wk", value: revenueBidData[cfg.trailingKeys["4 Wk"]] },
            { label: "3 Mo", value: revenueBidData[cfg.trailingKeys["3 Mo"]] },
            { label: "6 Mo", value: revenueBidData[cfg.trailingKeys["6 Mo"]] },
            { label: "12 Mo", value: revenueBidData[cfg.trailingKeys["12 Mo"]] },
            { label: revenueBidData.fy_label_this, value: revenueBidData[cfg.trailingKeys["_this_fy"]] },
            { label: revenueBidData.fy_label_last, value: revenueBidData[cfg.trailingKeys["_last_fy"]] },
          ];
          const bidCumulativeTotal = revenueBidData[cfg.fyTotalKey] ?? 0;
          // Hit/miss against pace: weeks elapsed in CURRENT FY × weekly_goal.
          // revenueBidData.weeks contains both last_fy + this_fy, so filter to current FY only.
          const fyThisStartIso = (() => {
            const today = new Date();
            const y = today.getMonth() >= 4 ? today.getFullYear() : today.getFullYear() - 1;
            return `${y}-05-01`;
          })();
          const bidWeeksInCurrentFY = (revenueBidData.weeks || []).filter(w => w.week_start >= fyThisStartIso).length;
          const bidExpectedPace = bidWeeksInCurrentFY * (revenueBidData.weekly_goal ?? 0);
          const isBidCumulativeHit = bidMetric === "sales" ? (bidExpectedPace > 0 && bidCumulativeTotal >= bidExpectedPace) : true;
          // Short label + half-million rounding, e.g. "FY27 $10.5M / $10M"
          const shortFyLabel = (label) => (label || "").replace(/^FY20(\d{2})$/, "FY$1");
          const formatHalfM = (v) => {
            const m = Math.round((v || 0) / 500000) * 0.5;
            return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
          };
          // Trailing metric compare threshold — only Revenue $ has a per-week goal; GM $ / GM % use neutral gray.
          const trailingHit = (v) => bidMetric === "sales" && v >= revenueBidData.weekly_goal;
          return (
          <div className="bg-white rounded-lg border border-slate-200" data-testid="revenue-bid-section">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-y-2">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-blue-500" />
                Revenue Bid
                <MetricToggle value={bidMetric} onChange={setBidMetric} testid="bid-metric-toggle" />
                <GrainToggle value={bidGrain} onChange={setBidGrain} testid="bid-grain-toggle" />
                {bidMetric === "sales" && (
                  <span className={`ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border ${
                    isBidCumulativeHit ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
                  }`}
                    data-testid="bid-cumulative-total">
                    <span className={`text-xs font-bold tabular-nums ${
                      isBidCumulativeHit ? "text-emerald-700" : "text-red-700"
                    }`}>{shortFyLabel(revenueBidData.fy_label_this)} {formatHalfM(bidCumulativeTotal)} / {formatHalfM(bidExpectedPace)}</span>
                  </span>
                )}
                {bidMetric !== "sales" && (
                  <span className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border bg-slate-50 border-slate-200"
                    data-testid="bid-cumulative-total">
                    <span className="text-xs font-bold tabular-nums text-slate-700">
                      {shortFyLabel(revenueBidData.fy_label_this)} {cfg.format(bidCumulativeTotal)}
                    </span>
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-3 flex-wrap">
                {trailingMetrics.map(m => (
                  <div key={m.label} className="flex items-center gap-1">
                    <span className="text-[10px] text-slate-400 uppercase tracking-wide">{m.label}:</span>
                    <span className={cn("text-xs font-bold tabular-nums", bidMetric === "sales" ? (trailingHit(m.value) ? "text-emerald-600" : "text-red-500") : "text-slate-700")}
                      data-testid={`trailing-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
                      {cfg.format(m.value)}
                    </span>
                  </div>
                ))}
                <div className="flex items-center gap-1.5 text-xs text-slate-400 ml-1">
                  {bidMetric === "sales" ? (
                    <>
                      <div className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Hit
                      <div className="h-2.5 w-2.5 rounded-sm bg-red-400 ml-1" /> Miss
                      <span className="ml-1 text-slate-300">|</span>
                      <span className="ml-1">Goal: {fmt$(activeGoal)}/{useMonth ? "mo" : "wk"}</span>
                    </>
                  ) : (
                    <span>{cfg.tooltipLabel} view · weighted avg</span>
                  )}
                </div>
              </div>
            </div>
            <div className="p-5">
              <div className="flex">
                {/* Frozen Y-axis pane — always visible */}
                <div className="flex-shrink-0 bg-white z-10 border-r border-slate-100" style={{ width: 70 }}>
                  <div style={{ height: 280, paddingTop: 10 }}>
                    {(() => {
                      const ticks = [];
                      const step = yDomain[1] / 4;
                      for (let i = 0; i <= 4; i++) ticks.push(Math.round(step * i * 100) / 100);
                      const chartH = 220; // area between top margin and x-axis
                      const fmtTick = (v) => {
                        if (bidMetric === "gmpct") return `${v.toFixed(0)}%`;
                        return v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${Math.round(v / 1000)}K` : `$${v}`;
                      };
                      return (
                        <div className="relative" style={{ height: chartH }}>
                          {ticks.map(v => {
                            const pct = ((yDomain[1] - v) / yDomain[1]) * 100;
                            return (
                              <div key={v} className="absolute right-0 pr-1 text-[10px] text-slate-400 tabular-nums text-right w-full"
                                style={{ top: `${pct}%`, transform: "translateY(-50%)" }}>
                                {fmtTick(v)}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                {/* Scrollable chart — 12 weeks visible, scroll left for history */}
                <div className="flex-1 overflow-x-auto" style={{ direction: "rtl" }} data-testid="revenue-bid-scroll">
                  <div style={{ direction: "ltr", width: Math.max(allWeeks.length * 56, 672) }}>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={allWeeks} margin={{ top: 10, right: 10, bottom: 40, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 10, fill: "#94a3b8" }}
                          tickLine={false}
                          axisLine={{ stroke: "#e2e8f0" }}
                          angle={-45}
                          textAnchor="end"
                          height={50}
                        />
                        <YAxis domain={yDomain} hide={true} />
                        <Tooltip
                          contentStyle={{ fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
                          formatter={(value) => [cfg.format(value), cfg.tooltipLabel]}
                          labelFormatter={(label) => `${useMonth ? "Month" : "Week of"} ${label}`}
                        />
                        {cfg.goal && (
                          <ReferenceLine
                            y={activeGoal}
                            stroke="#6366f1"
                            strokeDasharray="4 4"
                            strokeWidth={1.5}
                            label={{ value: `Goal: ${fmt$(activeGoal)}${useMonth ? '/mo' : ''}`, position: "right", fontSize: 10, fill: "#6366f1" }}
                          />
                        )}
                        <Bar dataKey={cfg.dataKey} radius={[4, 4, 0, 0]} maxBarSize={48}>
                          {allWeeks.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={cfg.colorByHit ? (entry.hit_goal ? "#10b981" : "#f87171") : cfg.barColor} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* REVENUE BOOKED - Weekly Bar Chart (Won projects by won_date) */}
        {revenueBookedData && (() => {
          const cfg = metricConfig(bookedMetric, revenueBookedData.weekly_goal);
          const useMonth = bookedGrain === "month";
          const allWeeks = useMonth ? (revenueBookedData.months || []) : revenueBookedData.weeks;
          const activeGoal = useMonth ? (revenueBookedData.monthly_goal || cfg.goal * 4.33) : cfg.goal;
          const dataMax = Math.max(0, ...allWeeks.map(w => w[cfg.dataKey] || 0));
          const goalRef = cfg.goal || 0;
          let yDomain;
          if (bookedMetric === "gmpct") {
            yDomain = [0, Math.max(50, Math.ceil(dataMax / 10) * 10)];
          } else if (bookedMetric === "gm") {
            const step = useMonth ? 200000 : 50000;
            yDomain = [0, Math.max(step, Math.ceil(dataMax / step) * step)];
          } else {
            const step = useMonth ? 200000 : 50000;
            yDomain = [0, Math.ceil(Math.max(dataMax, (activeGoal || 0) * 1.1) / step) * step || (step * 2)];
          }
          const trailingMetrics = [
            { label: "4 Wk", value: revenueBookedData[cfg.trailingKeys["4 Wk"]] },
            { label: "3 Mo", value: revenueBookedData[cfg.trailingKeys["3 Mo"]] },
            { label: "6 Mo", value: revenueBookedData[cfg.trailingKeys["6 Mo"]] },
            { label: "12 Mo", value: revenueBookedData[cfg.trailingKeys["12 Mo"]] },
            { label: revenueBookedData.fy_label_this, value: revenueBookedData[cfg.trailingKeys["_this_fy"]] },
            ...(revenueBookedData.fy_label_last
              ? [{ label: revenueBookedData.fy_label_last, value: revenueBookedData[cfg.trailingKeys["_last_fy"]] }]
              : []),
          ];
          const cumulativeTotal = revenueBookedData[cfg.fyTotalKey] ?? 0;
          // Hit/miss against pace: weeks_elapsed * weekly_goal.
          const weeksElapsedThisFY = (revenueBookedData.weeks || []).length;
          const expectedPaceTotal = weeksElapsedThisFY * (revenueBookedData.weekly_goal ?? 0);
          const isCumulativeHit = bookedMetric === "sales" ? (expectedPaceTotal > 0 && cumulativeTotal >= expectedPaceTotal) : true;
          // Short label + half-million rounding helpers
          const shortFyLabelBk = (label) => (label || "").replace(/^FY20(\d{2})$/, "FY$1");
          const formatHalfMBk = (v) => {
            const m = Math.round((v || 0) / 500000) * 0.5;
            return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`;
          };
          const trailingHit = (v) => bookedMetric === "sales" && v >= revenueBookedData.weekly_goal;
          return (
          <div className="bg-white rounded-lg border border-slate-200" data-testid="revenue-booked-section">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between flex-wrap gap-y-2">
              <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Trophy className="h-4 w-4 text-emerald-500" />
                Revenue Booked
                <MetricToggle value={bookedMetric} onChange={setBookedMetric} testid="booked-metric-toggle" />
                <GrainToggle value={bookedGrain} onChange={setBookedGrain} testid="booked-grain-toggle" />
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 gap-1 text-[11px] px-2 border-slate-200"
                  onClick={() => setReportOpen(true)}
                  data-testid="booked-report-btn"
                >
                  <Download className="h-3 w-3" />
                  Report
                </Button>
                {bookedMetric === "sales" && (
                  <span className={`ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border ${
                    isCumulativeHit ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"
                  }`}
                    data-testid="booked-cumulative-total">
                    <span className={`text-xs font-bold tabular-nums ${
                      isCumulativeHit ? "text-emerald-700" : "text-red-700"
                    }`}>{shortFyLabelBk(revenueBookedData.fy_label_this)} {formatHalfMBk(cumulativeTotal)} / {formatHalfMBk(expectedPaceTotal)}</span>
                  </span>
                )}
                {bookedMetric !== "sales" && (
                  <span className="ml-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border bg-slate-50 border-slate-200"
                    data-testid="booked-cumulative-total">
                    <span className="text-xs font-bold tabular-nums text-slate-700">
                      {shortFyLabelBk(revenueBookedData.fy_label_this)} {cfg.format(cumulativeTotal)}
                    </span>
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-3 flex-wrap">
                {trailingMetrics.map(m => (
                  <div key={m.label} className="flex items-center gap-1">
                    <span className="text-[10px] text-slate-400 uppercase tracking-wide">{m.label}:</span>
                    <span className={cn("text-xs font-bold tabular-nums", bookedMetric === "sales" ? (trailingHit(m.value) ? "text-emerald-600" : "text-red-500") : "text-slate-700")}
                      data-testid={`booked-trailing-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
                      {cfg.format(m.value)}
                    </span>
                  </div>
                ))}
                <div className="flex items-center gap-1.5 text-xs text-slate-400 ml-1">
                  {bookedMetric === "sales" ? (
                    <>
                      <div className="h-2.5 w-2.5 rounded-sm bg-emerald-500" /> Hit
                      <div className="h-2.5 w-2.5 rounded-sm bg-red-400 ml-1" /> Miss
                      <span className="ml-1 text-slate-300">|</span>
                      <span className="ml-1">Goal: {fmt$(activeGoal)}/{useMonth ? "mo" : "wk"}</span>
                    </>
                  ) : (
                    <span>{cfg.tooltipLabel} view · weighted avg</span>
                  )}
                </div>
              </div>
            </div>
            <div className="p-5">
              <div className="flex">
                {/* Frozen Y-axis pane */}
                <div className="flex-shrink-0 bg-white z-10 border-r border-slate-100" style={{ width: 70 }}>
                  <div style={{ height: 280, paddingTop: 10 }}>
                    {(() => {
                      const ticks = [];
                      const step = yDomain[1] / 4;
                      for (let i = 0; i <= 4; i++) ticks.push(Math.round(step * i * 100) / 100);
                      const chartH = 220;
                      const fmtTick = (v) => {
                        if (bookedMetric === "gmpct") return `${v.toFixed(0)}%`;
                        return v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${Math.round(v / 1000)}K` : `$${v}`;
                      };
                      return (
                        <div className="relative" style={{ height: chartH }}>
                          {ticks.map(v => {
                            const pct = ((yDomain[1] - v) / yDomain[1]) * 100;
                            return (
                              <div key={v} className="absolute right-0 pr-1 text-[10px] text-slate-400 tabular-nums text-right w-full"
                                style={{ top: `${pct}%`, transform: "translateY(-50%)" }}>
                                {fmtTick(v)}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                {/* Scrollable chart — 12 weeks visible, scroll left for history */}
                <div className="flex-1 overflow-x-auto" style={{ direction: "rtl" }} data-testid="revenue-booked-scroll">
                  <div style={{ direction: "ltr", width: Math.max(allWeeks.length * 56, 672) }}>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={allWeeks} margin={{ top: 10, right: 10, bottom: 40, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis
                          dataKey="label"
                          tick={{ fontSize: 10, fill: "#94a3b8" }}
                          tickLine={false}
                          axisLine={{ stroke: "#e2e8f0" }}
                          angle={-45}
                          textAnchor="end"
                          height={50}
                        />
                        <YAxis domain={yDomain} hide={true} />
                        <Tooltip
                          contentStyle={{ fontSize: 12, border: "1px solid #e2e8f0", borderRadius: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }}
                          formatter={(value) => [cfg.format(value), cfg.tooltipLabel]}
                          labelFormatter={(label) => `${useMonth ? "Month" : "Week of"} ${label}`}
                        />
                        {cfg.goal && (
                          <ReferenceLine
                            y={activeGoal}
                            stroke="#6366f1"
                            strokeDasharray="4 4"
                            strokeWidth={1.5}
                            label={{ value: `Goal: ${fmt$(activeGoal)}${useMonth ? '/mo' : ''}`, position: "right", fontSize: 10, fill: "#6366f1" }}
                          />
                        )}
                        <Bar dataKey={cfg.dataKey} radius={[4, 4, 0, 0]} maxBarSize={48}>
                          {allWeeks.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={cfg.colorByHit ? (entry.hit_goal ? "#10b981" : "#f87171") : cfg.barColor} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
          );
        })()}

        {/* HIT RATE CARDS */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="summary-cards">
          <StatCard icon={DollarSign} label="Hit Rate $" value={fmtPct(companyStats?.hit_rate_dollars)} subtitle={`${fmt$(companyStats?.won_revenue)} won of ${fmt$(companyStats?.total_bid_revenue)} bid`} color="bg-[#007BFF]" />
          <StatCard icon={Hash} label="Hit Rate Count" value={fmtPct(companyStats?.hit_rate_count)} subtitle={`${companyStats?.won_count || 0} won of ${companyStats?.total_bid_count || 0} bid`} color="bg-emerald-500" />
          <StatCard icon={TrendingUp} label="Won" value={`${companyStats?.won_count || 0} projects`} subtitle={fmt$(companyStats?.won_revenue)} color="bg-green-500" />
          <StatCard icon={TrendingDown} label="Lost / Cancelled" value={`${companyStats?.lost_count || 0} projects`} subtitle={fmt$(companyStats?.lost_revenue)} color="bg-red-500" />
        </div>

        {/* HIT RATE PERIOD BARS */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="hit-rate-dollar-periods">
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><DollarSign className="h-4 w-4 text-[#007BFF]" />Hit Rate $ by Period</h3>
            <div className="space-y-4">
              {Object.entries(periodLabels).map(([key, label]) => {
                const stats = data?.company?.[key];
                return (
                  <div key={key} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className={cn("text-xs font-medium", key === period ? "text-[#007BFF]" : "text-slate-500")}>{label}</span>
                      <span className="text-sm font-bold text-slate-900">{fmtPct(stats?.hit_rate_dollars)}</span>
                    </div>
                    <HitRateBar value={stats?.hit_rate_dollars || 0} color="bg-[#007BFF]" />
                    <p className="text-[10px] text-slate-400">{fmt$(stats?.won_revenue)} / {fmt$(stats?.total_bid_revenue)}</p>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-slate-200 p-5" data-testid="hit-rate-count-periods">
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2"><Hash className="h-4 w-4 text-emerald-500" />Hit Rate Count by Period</h3>
            <div className="space-y-4">
              {Object.entries(periodLabels).map(([key, label]) => {
                const stats = data?.company?.[key];
                return (
                  <div key={key} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className={cn("text-xs font-medium", key === period ? "text-emerald-600" : "text-slate-500")}>{label}</span>
                      <span className="text-sm font-bold text-slate-900">{fmtPct(stats?.hit_rate_count)}</span>
                    </div>
                    <HitRateBar value={stats?.hit_rate_count || 0} color="bg-emerald-500" />
                    <p className="text-[10px] text-slate-400">{stats?.won_count || 0} / {stats?.total_bid_count || 0} projects</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* QUARTER PIPELINE */}
        <div className="bg-white rounded-lg border border-slate-200" data-testid="quarter-pipeline">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-violet-500" />
              Slow Quarter Pipeline
              {quarterData && <span className="text-xs font-normal text-slate-400 ml-2">({quarterData.count} projects, {fmt$(quarterData.total_pipeline)} total)</span>}
            </h3>
            <div className="flex items-center gap-2">
              <Select value={quarterFy} onValueChange={setQuarterFy}>
                <SelectTrigger className="h-8 w-28 text-xs border-slate-200" data-testid="quarter-fy-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="this">This FY</SelectItem>
                  <SelectItem value="next">Next FY</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex bg-slate-100 rounded-md p-0.5 gap-0.5">
                {[1, 2, 3, 4].map(q => (
                  <button key={q} className={cn("px-3 py-1 text-xs font-medium rounded transition-colors",
                    quarterNum === q ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700")}
                    onClick={() => setQuarterNum(q)} data-testid={`quarter-btn-${q}`}>
                    Q{q}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="max-h-[350px] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-white/95 backdrop-blur-sm z-10">
                <TableRow className="border-b-2 border-slate-200 hover:bg-transparent">
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-5 py-3">Project</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3">Customer</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3">AM</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 text-right">Total Sales</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 text-right">Profit %</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 text-right">Prob %</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 text-right">Forecast Rev</TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3">Completion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {quarterData?.projects?.length > 0 ? quarterData.projects.map((p, i) => (
                  <TableRow key={i} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`quarter-row-${i}`}>
                    <TableCell className="px-5 py-3 font-medium text-slate-900 text-sm truncate max-w-[220px]">{p.name}</TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-600 truncate max-w-[160px]">{p.customer_name}</TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-600 font-medium">{p.account_manager || "--"}</TableCell>
                    <TableCell className="px-4 py-3 text-right text-sm font-medium text-slate-700 tabular-nums">{fmt$(p.total_sales)}</TableCell>
                    <TableCell className="px-4 py-3 text-right text-sm text-slate-600 tabular-nums">{p.profit_margin?.toFixed(1)}%</TableCell>
                    <TableCell className="px-4 py-3 text-right text-sm text-slate-600 tabular-nums">{p.probability}%</TableCell>
                    <TableCell className="px-4 py-3 text-right text-sm font-medium text-slate-700 tabular-nums">{fmt$(p.forecasted_revenue)}</TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-600">
                      {p.completion ? new Date(p.completion + "T00:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "--"}
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={8} className="h-24 text-center text-sm text-slate-400">
                    No projects over $150K with completion dates in {quarterData?.quarter_label || "this quarter"}
                  </TableCell></TableRow>
                )}
              </TableBody>
              {quarterData?.projects?.length > 0 && (
                <tfoot className="bg-slate-50 border-t-2 border-slate-300">
                  <tr>
                    <td className="px-5 py-3 text-xs font-bold text-slate-700 uppercase" colSpan={3}>Totals ({quarterData.count})</td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-slate-900 tabular-nums">{fmt$(quarterData.total_pipeline)}</td>
                    <td colSpan={2}></td>
                    <td className="px-4 py-3 text-right text-sm font-bold text-slate-900 tabular-nums">{fmt$(quarterData.total_forecasted)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
        </div>

        {/* CUSTOMER TABLE - sortable */}
        <div className="bg-white rounded-lg border border-slate-200" data-testid="customer-breakdown">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" />Hit Rate by Customer</h3>
            <div className="flex items-center gap-3">
              <PeriodTabs active={period} onChange={setPeriod} labels={periodLabels} />
              <span className="text-xs text-slate-400">{customerRows.length} customers</span>
              <SearchBar value={customerSearch} onChange={setCustomerSearch} width="w-56"
                         placeholder="Search..." testId="customer-search" />
            </div>
          </div>
          <div className="max-h-[450px] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-white/95 backdrop-blur-sm z-10">
                <TableRow className="border-b-2 border-slate-200 hover:bg-transparent">
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-5 py-3">
                    <SortableHeader label="Customer Name" sortKey="name" sortState={customerSort} onSort={handleCustomerSort} />
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 text-right">
                    <SortableHeader label="Total Bid" sortKey="total_bid_revenue" sortState={customerSort} onSort={handleCustomerSort} align="right" />
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 text-right">
                    <SortableHeader label="Won" sortKey="won_revenue" sortState={customerSort} onSort={handleCustomerSort} align="right" />
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 text-right">
                    <SortableHeader label="Lost" sortKey="lost_revenue" sortState={customerSort} onSort={handleCustomerSort} align="right" />
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 text-center w-[180px]">
                    <SortableHeader label="Hit Rate $" sortKey="hit_rate_dollars" sortState={customerSort} onSort={handleCustomerSort} />
                  </TableHead>
                  <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-4 py-3 text-center w-[180px]">
                    <SortableHeader label="Hit Rate Count" sortKey="hit_rate_count" sortState={customerSort} onSort={handleCustomerSort} />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customerRows.length > 0 ? customerRows.map((c, i) => (
                  <TableRow key={c.name} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`customer-row-${i}`}>
                    <TableCell className="px-5 py-3 font-medium text-slate-900 text-sm">{c.name}</TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      <div className="text-sm font-medium text-slate-700 tabular-nums">{fmt$(c.total_bid_revenue)}</div>
                      <div className="text-[10px] text-slate-400">{c.total_bid_count} project{c.total_bid_count !== 1 ? 's' : ''}</div>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      <div className="text-sm font-medium text-green-600 tabular-nums">{fmt$(c.won_revenue)}</div>
                      <div className="text-[10px] text-slate-400">{c.won_count}</div>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right">
                      <div className="text-sm font-medium text-red-600 tabular-nums">{fmt$(c.lost_revenue)}</div>
                      <div className="text-[10px] text-slate-400">{c.lost_count}</div>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <HitRateBar value={c.hit_rate_dollars} color="bg-[#007BFF]" />
                        <span className="text-xs font-bold text-slate-700 w-12 text-right tabular-nums">{fmtPct(c.hit_rate_dollars)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <HitRateBar value={c.hit_rate_count} color="bg-emerald-500" />
                        <span className="text-xs font-bold text-slate-700 w-12 text-right tabular-nums">{fmtPct(c.hit_rate_count)}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={6} className="h-24 text-center text-sm text-slate-400">No customer data for this period</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* FORECAST REVENUE - 24 Month View */}
        <div className="bg-white rounded-lg border border-slate-200" data-testid="revenue-forecast">
          <div className="p-5 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              Revenue Forecast - Rolling 24 Months
              {forecastData && <span className="text-xs font-normal text-slate-400 ml-2">Total: {fmt$(forecastData.total_24_month)}</span>}
            </h3>
            <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" onClick={handleCopyForecast} data-testid="copy-forecast-btn">
              <Copy className="h-3.5 w-3.5" />
              Copy to Clipboard
            </Button>
          </div>
          <div className="p-5">
            {forecastData?.months?.length > 0 ? (
              <>
                {/* Revenue Forecast Chart */}
                <div className="mb-6" data-testid="revenue-forecast-chart">
                  <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="h-4 w-4 text-slate-400" />
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Revenue Projection</h4>
                  </div>
                  <div className="h-[280px] w-full">
                    <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
                      <AreaChart
                        data={forecastData.months}
                        margin={{ top: 10, right: 30, left: 10, bottom: 30 }}
                      >
                        <defs>
                          <linearGradient id="forecastGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                        <XAxis 
                          dataKey="month_label" 
                          tick={{ fontSize: 10, fill: '#64748b' }}
                          tickLine={false}
                          axisLine={{ stroke: '#e2e8f0' }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                          interval={1}
                        />
                        <YAxis 
                          tick={{ fontSize: 10, fill: '#64748b' }}
                          tickLine={false}
                          axisLine={false}
                          tickFormatter={(value) => value >= 1000000 ? `$${(value / 1000000).toFixed(1)}M` : value >= 1000 ? `$${(value / 1000).toFixed(0)}K` : `$${value}`}
                          width={65}
                        />
                        <Tooltip 
                          formatter={(value) => [fmt$(value), 'Forecast']}
                          labelStyle={{ color: '#334155', fontWeight: 600 }}
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                          }}
                        />
                        <ReferenceLine 
                          x={forecastData.months[11]?.month_label} 
                          stroke="#94a3b8" 
                          strokeDasharray="5 5"
                          label={{ value: 'Year 2', position: 'top', fill: '#64748b', fontSize: 10 }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="amount" 
                          stroke="#10b981" 
                          strokeWidth={2}
                          fill="url(#forecastGradient)" 
                          dot={false}
                          activeDot={{ r: 5, fill: '#10b981', stroke: 'white', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="flex justify-center gap-8 mt-2 text-xs text-slate-500">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500/30 border border-emerald-500"></div>
                      <span>Year 1: {fmt$(forecastData.months.slice(0, 12).reduce((sum, m) => sum + m.amount, 0))}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-slate-300 border border-slate-400"></div>
                      <span>Year 2: {fmt$(forecastData.months.slice(12, 24).reduce((sum, m) => sum + m.amount, 0))}</span>
                    </div>
                  </div>
                </div>

                {/* Monthly Data Tables */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* First 12 months */}
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Months 1-12</h4>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-slate-200 hover:bg-transparent">
                          <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-3 py-2">Month</TableHead>
                          <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-3 py-2 text-right">Forecast</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {forecastData.months.slice(0, 12).map((m, i) => (
                          <TableRow key={m.month_key} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`forecast-row-${i}`}>
                            <TableCell className="px-3 py-2 text-sm text-slate-700">{m.month_label}</TableCell>
                            <TableCell className="px-3 py-2 text-right text-sm font-medium text-slate-900 tabular-nums">{fmt$(m.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <tfoot className="bg-slate-50 border-t border-slate-200">
                        <tr>
                          <td className="px-3 py-2 text-xs font-bold text-slate-700">Year 1 Total</td>
                          <td className="px-3 py-2 text-right text-sm font-bold text-slate-900 tabular-nums">
                            {fmt$(forecastData.months.slice(0, 12).reduce((sum, m) => sum + m.amount, 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                  {/* Months 13-24 */}
                  <div>
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Months 13-24</h4>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-b border-slate-200 hover:bg-transparent">
                          <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-3 py-2">Month</TableHead>
                          <TableHead className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 px-3 py-2 text-right">Forecast</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {forecastData.months.slice(12, 24).map((m, i) => (
                          <TableRow key={m.month_key} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`forecast-row-${i + 12}`}>
                            <TableCell className="px-3 py-2 text-sm text-slate-700">{m.month_label}</TableCell>
                            <TableCell className="px-3 py-2 text-right text-sm font-medium text-slate-900 tabular-nums">{fmt$(m.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <tfoot className="bg-slate-50 border-t border-slate-200">
                        <tr>
                          <td className="px-3 py-2 text-xs font-bold text-slate-700">Year 2 Total</td>
                          <td className="px-3 py-2 text-right text-sm font-bold text-slate-900 tabular-nums">
                            {fmt$(forecastData.months.slice(12, 24).reduce((sum, m) => sum + m.amount, 0))}
                          </td>
                        </tr>
                      </tfoot>
                    </Table>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-400 text-center py-8">No forecast data available</p>
            )}
          </div>
        </div>

        {/* COMPANY FORECAST — P&L from backlog report */}
        <CompanyForecast />
      </div>

      {/* Revenue Booked Report Export Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-md" data-testid="booked-report-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm flex items-center gap-2">
              <Download className="h-4 w-4 text-emerald-500" />
              Export Revenue Booked report
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs text-slate-500">
              CSV of every Won project (job name, revenue used in the chart, GM $ and GM %) with a Won date in this range.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">Start date</Label>
                <Input
                  type="date"
                  max={reportRange.end || todayIso}
                  value={reportRange.start}
                  onChange={(e) => setReportRange((r) => ({ ...r, start: e.target.value }))}
                  data-testid="booked-report-start"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">End date</Label>
                <Input
                  type="date"
                  min={reportRange.start}
                  value={reportRange.end}
                  onChange={(e) => setReportRange((r) => ({ ...r, end: e.target.value }))}
                  data-testid="booked-report-end"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {[
                { id: "this-month", label: "This month", start: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`; })(), end: todayIso },
                { id: "this-quarter", label: "This quarter", start: (() => { const d = new Date(); const qm = Math.floor(d.getMonth() / 3) * 3; return `${d.getFullYear()}-${String(qm + 1).padStart(2, "0")}-01`; })(), end: todayIso },
                { id: "this-fy", label: "This FY", start: (() => { const d = new Date(); const y = d.getMonth() >= 4 ? d.getFullYear() : d.getFullYear() - 1; return `${y}-05-01`; })(), end: todayIso },
                { id: "may-jul-current", label: "5/1 – 7/31", start: (() => { const d = new Date(); const y = d.getMonth() >= 4 ? d.getFullYear() : d.getFullYear() - 1; return `${y}-05-01`; })(), end: (() => { const d = new Date(); const y = d.getMonth() >= 4 ? d.getFullYear() : d.getFullYear() - 1; return `${y}-07-31`; })() },
              ].map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => setReportRange({ start: preset.start, end: preset.end })}
                  className="text-[11px] h-6 px-2 rounded-md border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-600"
                  data-testid={`booked-report-preset-${preset.id}`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setReportOpen(false)} data-testid="booked-report-cancel">
                Cancel
              </Button>
              <Button
                size="sm"
                className="bg-[#007BFF] hover:bg-[#0069d9] text-white gap-1.5"
                disabled={downloading}
                onClick={downloadReport}
                data-testid="booked-report-download"
              >
                <Download className="h-3.5 w-3.5" />
                {downloading ? "Preparing…" : "Download CSV"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
